import { Temporal } from '@js-temporal/polyfill';
import { z } from 'zod';

import type { RecordData } from './model';

export const dateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine(s => {
    const date = new Date(`${s}T12:00:00Z`);

    return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === s;
  }, 'Choose a valid date');

const instant = z
  .string()
  .datetime()
  .transform(value => new Date(value).toISOString());

const intervalSchema = z
  .object({ startsAt: instant, endsAt: instant })
  .refine(v => v.startsAt < v.endsAt, 'End must be after start');

export const calendarSchema = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('booking'),
      zone: z.string().optional(),
      personId: z.string().uuid().nullable().optional(),
      followUpTaskIds: z.array(z.string().uuid()).max(500).optional(),
      recurrence: z
        .object({
          seriesId: z.string().uuid(),
          frequency: z.enum(['daily', 'weekly', 'monthly', 'yearly']),
          interval: z.number().int().min(1).max(52),
          count: z.number().int().min(1).max(366),
          index: z.number().int().nonnegative(),
          originalStartsAt: instant,
          zone: z.string(),
        })
        .optional(),
      title: z.string().trim().min(1).max(500),
      startsAt: instant,
      endsAt: instant,
      taskId: z.string().uuid().nullable(),
      noteId: z.string().uuid().nullable(),
      notes: z.string().max(5000),
      reminderMinutes: z.number().int().min(0).max(10080),
      cancelled: z.boolean(),
      history: z.array(intervalSchema).max(500),
    })
    .refine(v => v.startsAt < v.endsAt, 'End must be after start'),
  z.object({
    type: z.literal('availability'),
    day: dateSchema,
    zone: z.string().max(100),
    windows: z.array(intervalSchema).max(12),
  }),
  z
    .object({
      type: z.literal('birthday'),
      personId: z.string().uuid().nullable().optional(),
      title: z.string().trim().min(1).max(500),
      month: z.number().int().min(1).max(12),
      day: z.number().int().min(1).max(31),
      reminderDays: z.number().int().min(0).max(365),
      notes: z.string().max(5000),
      cancelled: z.boolean(),
    })
    .refine(
      v => new Date(Date.UTC(2000, v.month - 1, v.day)).getUTCMonth() === v.month - 1,
      'Choose a valid birthday',
    ),
]);

export type CalendarItem = z.infer<typeof calendarSchema>;
export type Booking = Extract<CalendarItem, { type: 'booking' }>;
export type Availability = Extract<CalendarItem, { type: 'availability' }>;
export type Birthday = Extract<CalendarItem, { type: 'birthday' }>;
export type Interval = { startsAt: string; endsAt: string };

export class CalendarConflict extends Error {
  constructor(
    message: string,
    public recordIds: string[] = [],
  ) {
    super(message);
    this.name = 'CalendarConflict';
  }
}

export const overlaps = (a: Interval, b: Interval) =>
  a.startsAt < b.endsAt && b.startsAt < a.endsAt;

export const contains = (outer: Interval, inner: Interval) =>
  outer.startsAt <= inner.startsAt && inner.endsAt <= outer.endsAt;

export function validateCalendarState(
  records: Pick<RecordData, 'id' | 'kind' | 'value'>[],
  now = Date.now(),
) {
  const calendar = records
    .filter(r => r.kind === 'calendar')
    .map(r => ({ id: r.id, value: calendarSchema.parse(r.value) }));

  const windows = calendar.flatMap(r => (r.value.type === 'availability' ? r.value.windows : []));

  const bookings = calendar.filter(r => r.value.type === 'booking' && !r.value.cancelled) as {
    id: string;
    value: Booking;
  }[];

  for (let i = 0; i < bookings.length; i++) {
    const entry = bookings[i];

    for (const other of bookings.slice(i + 1)) {
      if (overlaps(entry.value, other.value)) {
        throw new CalendarConflict(
          `“${entry.value.title}” overlaps “${other.value.title}”. Move or shorten one of them.`,
          [entry.id, other.id],
        );
      }
    }

    if (
      entry.value.taskId &&
      !records.some(r => r.kind === 'task' && r.id === entry.value.taskId)
    ) {
      throw new CalendarConflict('The linked task is unavailable.', [entry.id]);
    }

    if (
      entry.value.noteId &&
      !records.some(r => r.kind === 'note' && r.id === entry.value.noteId)
    ) {
      throw new CalendarConflict('The linked note is unavailable.', [entry.id]);
    }

    if (
      entry.value.taskId &&
      Date.parse(entry.value.endsAt) > now &&
      !windows.some(w => contains(w, entry.value))
    ) {
      throw new CalendarConflict(`“${entry.value.title}” needs a fitting availability window.`, [
        entry.id,
      ]);
    }
  }

  for (const entry of calendar) {
    if (entry.value.type !== 'availability') {
      const personId = entry.value.personId;

      if (personId && !records.some(r => r.kind === 'person' && r.id === personId)) {
        throw new CalendarConflict('The linked person is unavailable.', [entry.id]);
      }

      if (entry.value.type === 'booking') {
        const booking = entry.value;

        if (
          (booking.followUpTaskIds ?? []).some(
            id => !records.some(r => r.kind === 'task' && r.id === id),
          )
        ) {
          throw new CalendarConflict('An appointment follow-up task is unavailable.', [entry.id]);
        }

        if (booking.zone) {
          try {
            new Intl.DateTimeFormat('en', { timeZone: booking.zone });
          } catch {
            throw new CalendarConflict('Invalid event timezone.', [entry.id]);
          }
        }
      }
    }
  }

  for (const entry of calendar) {
    if (entry.value.type === 'availability') {
      let formatter: Intl.DateTimeFormat;

      try {
        formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: entry.value.zone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });
      } catch {
        throw new CalendarConflict('Choose a valid timezone for availability.', [entry.id]);
      }

      const dateAt = (instant: number) => {
        const parts = formatter.formatToParts(new Date(instant));
        const part = (type: string) => parts.find(p => p.type === type)?.value;

        return `${part('year')}-${part('month')}-${part('day')}`;
      };

      const availabilityDay = entry.value.day;

      if (
        entry.value.windows.some(
          window =>
            dateAt(Date.parse(window.startsAt)) !== availabilityDay ||
            dateAt(Date.parse(window.endsAt) - 1) !== availabilityDay,
        )
      ) {
        throw new CalendarConflict(
          'Availability windows must stay within their selected day and timezone.',
          [entry.id],
        );
      }

      const sorted = [...entry.value.windows].sort((a, b) => a.startsAt.localeCompare(b.startsAt));

      for (let i = 1; i < sorted.length; i++) {
        if (overlaps(sorted[i - 1], sorted[i])) {
          throw new CalendarConflict('Availability windows overlap. Combine or shorten them.', [
            entry.id,
          ]);
        }
      }
    }
  }
}

export function freeSlots(
  windows: Interval[],
  bookings: Interval[],
  durationMinutes: number,
  earliest = Date.now(),
): Interval[] {
  if (!Number.isInteger(durationMinutes) || durationMinutes <= 0) {
    return [];
  }

  const duration = durationMinutes * 60000,
    slots: Interval[] = [];

  for (const window of [...windows].sort((a, b) => a.startsAt.localeCompare(b.startsAt))) {
    let cursor = Math.max(Date.parse(window.startsAt), earliest),
      end = Date.parse(window.endsAt);

    for (const busy of bookings
      .filter(b => overlaps(window, b))
      .sort((a, b) => a.startsAt.localeCompare(b.startsAt))) {
      if (cursor + duration <= Date.parse(busy.startsAt)) {
        slots.push({
          startsAt: new Date(cursor).toISOString(),
          endsAt: new Date(cursor + duration).toISOString(),
        });
      }

      cursor = Math.max(cursor, Date.parse(busy.endsAt));
    }

    if (cursor + duration <= end) {
      slots.push({
        startsAt: new Date(cursor).toISOString(),
        endsAt: new Date(cursor + duration).toISOString(),
      });
    }
  }

  return slots
    .filter((slot, index) => slots.findIndex(s => s.startsAt === slot.startsAt) === index)
    .slice(0, 6);
}

/**
 * Places each request in the earliest free slot that fits it, in the order
 * given — Set out's "Reserve time in my windows", as on the phone. Each
 * placement is booked before the next is looked at, so two stones never land
 * in the same slot; one too long for what is left is skipped, not squeezed.
 */
export function fitReservations(
  requests: { taskId: string; minutes: number }[],
  windows: Interval[],
  bookings: Interval[],
  earliest = Date.now(),
): { taskId: string; slot: Interval | null }[] {
  const busy = [...bookings];

  return requests.map(request => {
    const [slot = null] = freeSlots(windows, busy, request.minutes, earliest);

    if (slot) {
      busy.push(slot);
    }

    return { taskId: request.taskId, slot };
  });
}

/** The next quarter hour at or after `time`: a block reserved at 10:07 starts at 10:15. */
export function quarterHour(time = Date.now()) {
  const quarter = 15 * 60000;

  return Math.ceil(time / quarter) * quarter;
}

let workspaceZone: string | undefined;

export function setCalendarZone(zone: string) {
  Temporal.Now.zonedDateTimeISO(zone);
  workspaceZone = zone;
}

export const calendarZone = () => workspaceZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

export function localDay(date = new Date(), zone = calendarZone()) {
  return Temporal.Instant.from(date.toISOString())
    .toZonedDateTimeISO(zone)
    .toPlainDate()
    .toString();
}

export function localInput(iso: string, zone = calendarZone()) {
  return Temporal.Instant.from(iso)
    .toZonedDateTimeISO(zone)
    .toPlainDateTime()
    .toString({ smallestUnit: 'minute' });
}

export function localInstant(
  input: string,
  occurrence: '' | 'earlier' | 'later' = '',
  zone = calendarZone(),
) {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(input)) {
    throw new Error('Choose a date and time.');
  }

  const local = Temporal.PlainDateTime.from(input);

  const first = local.toZonedDateTime(zone, { disambiguation: 'earlier' }),
    last = local.toZonedDateTime(zone, { disambiguation: 'later' });

  if (!first.toPlainDateTime().equals(local) || !last.toPlainDateTime().equals(local)) {
    throw new Error('This clock time does not exist in your timezone. Choose another time.');
  }

  if (first.epochMilliseconds !== last.epochMilliseconds && !occurrence) {
    throw new Error('This clock time occurs twice. Choose the first or second occurrence below.');
  }

  return new Date((occurrence === 'later' ? last : first).epochMilliseconds).toISOString();
}

export function shiftDay(day: string, amount: number) {
  return Temporal.PlainDate.from(day).add({ days: amount }).toString();
}

export function dayRange(day: string): Interval {
  const date = Temporal.PlainDate.from(day);

  return {
    startsAt: new Date(date.toZonedDateTime(calendarZone()).epochMilliseconds).toISOString(),
    endsAt: new Date(
      date.add({ days: 1 }).toZonedDateTime(calendarZone()).epochMilliseconds,
    ).toISOString(),
  };
}

export function birthdayDate(value: Birthday, year: number) {
  // Proposed product default: February 29 is observed on February 28 in non-leap years.
  return Temporal.PlainDate.from(
    { year, month: value.month, day: value.day },
    { overflow: 'constrain' },
  ).toString();
}

export function reminderList(
  records: Pick<RecordData, 'id' | 'kind' | 'value'>[],
  now = new Date(),
) {
  const today = localDay(now),
    reminders: {
      id: string;
      title: string;
      detail: string;
      date: string;
      /** The appointment has begun: happening, not approaching. */
      started: boolean;
    }[] = [];

  for (const record of records) {
    if (record.kind !== 'calendar') {
      continue;
    }

    const item = record.value as CalendarItem;

    if (
      item.type === 'booking' &&
      !item.cancelled &&
      Date.parse(item.endsAt) > now.getTime() &&
      Date.parse(item.startsAt) - item.reminderMinutes * 60000 <= now.getTime()
    ) {
      const started = Date.parse(item.startsAt) <= now.getTime();

      reminders.push({
        id: record.id,
        title: item.title,
        detail: started
          ? 'Scheduled now'
          : `Starts at ${new Date(item.startsAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: calendarZone() })}`,
        date: localDay(new Date(item.startsAt)),
        started,
      });
    }

    if (item.type === 'birthday' && !item.cancelled) {
      let date = birthdayDate(item, Number(today.slice(0, 4)));

      if (date < today) {
        date = birthdayDate(item, Number(today.slice(0, 4)) + 1);
      }

      if (today >= shiftDay(date, -item.reminderDays)) {
        reminders.push({
          id: record.id,
          title: item.title,
          detail: date === today ? 'Birthday today' : `Birthday on ${date} · time to prepare`,
          date,
          started: false,
        });
      }
    }
  }

  return reminders.sort((a, b) => a.date.localeCompare(b.date));
}

export function historyForMove(
  original: Booking | undefined,
  next: Interval,
  now = Date.now(),
): Interval[] {
  if (!original) {
    return [];
  }

  const history = [...original.history];
  const moved = next.startsAt !== original.startsAt || next.endsAt !== original.endsAt;

  if (
    moved &&
    Date.parse(original.startsAt) < now &&
    !history.some(h => h.startsAt === original.startsAt && h.endsAt === original.endsAt)
  ) {
    history.push({ startsAt: original.startsAt, endsAt: original.endsAt });
  }

  return history;
}

/**
 * Minutes of live reserved time a task holds on one day.
 *
 * Only the part of each reservation that falls inside the day is counted, so a
 * block straddling midnight is not reported in full on both days. Cancelled
 * reservations and the intervals a rescheduled one left behind are plans that
 * no longer hold, and are left out.
 */
export function reservedMinutes(bookings: Booking[], taskId: string, range: Interval) {
  const milliseconds = bookings
    .filter(b => !b.cancelled && b.taskId === taskId && overlaps(b, range))
    .reduce(
      (total, b) =>
        total +
        Math.min(Date.parse(b.endsAt), Date.parse(range.endsAt)) -
        Math.max(Date.parse(b.startsAt), Date.parse(range.startsAt)),
      0,
    );

  return Math.round(milliseconds / 60000);
}
